import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ConfirmProvider } from 'material-ui-confirm';
import { ThemeProvider } from '@mui/material/styles';
import { useLocation, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material';
import { ascendingSort } from '../../../lib/Array';
import {
  buildRoomObjectAclsFromContentIds,
  getObjectAclsForContent,
} from '../../../components/Content/ContentAccess/helper';
import { CONSENT } from '../../../enums/RoomConsent';
import { composeFullname } from '../../../lib/DisplayName';
import {
  ContactSchema,
  OrganizationSchema,
  RoomJSONStructureItem,
  RoomSchema,
  SignableSchema,
  UserSchema,
} from '../../../definitions/schemas';
import useFolderData from '../../Folder/View/useFolderData';
import { DefaultDealName } from '../../../enums/Deals';
import { ErrorCodeList } from '../../../enums/ErrorCodeList';
import { Event, Events, Properties, Referrers } from '../../../enums/Analytics';
import { UnifiedConsentType } from '../../../enums/UnifiedConsentType';
import { getConsent, persistConsent } from '../../../lib/Consent';
import { getEmailQueryString } from '../../../lib/Query';
import { getNumericSpacing } from '../../../lib/Styles';
import { getRefreshQueryParams, getRoomType } from './helper';
import {
  getTrackingProperties,
  getViewAttemptedEvent,
  getViewedEvent,
  trackDeniedAccess,
} from './analytics';
import { HeadContext } from '../../../lib/context/HeadContext';
import { LayoutContext } from '../../../components/Layout/LayoutContext';
import { isDomainRedirectionRequired } from '../../../lib/helper/RoomHelper';
import { isDraftRoom, isRoomDisabled, isRoomPublic } from '../../../lib/Room';
import { isEmail } from '../../../lib/String';
import { isInternalRoom } from '../../../enums/RoomAudience';
import { isRoomSummaryDisabled } from '../../../components/Room/Layout/helper';
import { isVendorMode } from '../../../lib/Vendor';
import { loadOrganizationFonts } from '../../../lib/Organization';
import { QUERY_STRING } from '../../../enums/QueryString';
import {
  QueryErrorResponse,
  QueryErrorResponseMessage,
} from '../../../graphql/Interface';
import { RoomContentContextProps } from '../../../lib/RoomContentContext';
import { RoomLayout as RoomLayoutEnum } from '../../../enums/RoomLayout';
import { RoomStructureItemType } from '../../../enums/RoomStructureItemType';
import { RoomType } from '../../../enums/RoomTypes';
import {
  roomViewFields,
  roomViewInitialFields,
  verifyRoomUserAccessFields,
} from '../../../graphql/Rooms/RoomFields';
import { setHtmlLang } from '../../../lib/Language';
import { useI18n } from '../../../lib/hook/useI18n';
import Analytics, { EventProperty } from '../../../tools/Analytics';
import ConsentInterface from '../../../graphql/Rooms/ConsentInterface';
import ConsentPrompDialog from '../../../components/ConsentPromp/ConsentPrompDialog';
import ContentAccessList from '../../../components/Content/ContentAccess/ContentAccessList';
import FullScreenTrackingContextProvider from '../../../components/Rooms/Tracking/FullScreenTrackingContext';
import I18n from '../../../Services/I18n';
import MindTickleTheme from '../../../theme/MindTickleTheme';
import MindTickleThemeV2 from '../../../theme/MindTickleThemeV2';
import OrganizationsStore from '../../../stores/observables/OrganizationsStore';
import OrganizationThemeContextProvider from '../../../lib/context/OrganizationThemeContext';
import RoomEmptyState from '../../../components/Room/View/RoomEmptyState';
import RoomLayout from '../../../components/Room/Layout/RoomLayout';
import RoomLayoutSkeleton from '../../../components/Room/Layout/RoomLayoutSkeleton';
import RoomSecurity, {
  AccessConfirmationData,
  PersistedAccessData,
} from '../../../components/Room/View/Security';
import RoomsInterface, {
  RoomAccessData,
} from '../../../graphql/Rooms/RoomsInterface';
import RoomViewContextProvider from '../../../components/Room/View/RoomViewContext';
import RoomViewController from '../../../components/Room/Layout/RoomViewController';
import SecurityDialogWrapper from '../../../components/Room/View/SecurityDialogWrapper';
import SnackbarStore from '../../../stores/observables/SnackbarStore';
import useQuery from '../../../lib/hook/router/useQuery';
import UserStatus from '../../../enums/UserStatus';
import UserStore, {
  UserRolesStore,
} from '../../../stores/observables/UserStore';
import VerifyPinCodeDialog from '../../../components/Room/View/VerifyPinCodeDialog';
import WindowFocusContextProvider from '../../../components/Rooms/Tracking/WindowFocusContext';
import GlobalCopilot, {
  CopilotActionProvider,
  CopilotMethodsProvider,
} from '../../../components/GlobalCopilot';
import { getConversationAuthor } from '../../../components/Conversation/helper';
import useCopilotVisibility from '../../../components/GlobalCopilot/hooks/useCopilotVisibility';
import useRoomContentsPool from '../../../components/GlobalCopilot/hooks/useRoomContentsPool';

const MAX_ACCESS_ATTEMPTS = 3;

/**
 * @warning when adding new processes that require information to be loaded,
 * make sure to test these different scenarios:
 *   1. Anonymous visitor: no room security settings.
 *   2. Contact visitor: at least room.requires_email, room.verify_email,
 *      room.passcode or room.invited_contacts_only. Or, being an anoymous
 *      visitor and entering the email (e.g. trying to leave a comment).
 *   3. Logged in user: visiting a room from the same organization as the
 *      currently logged user (no vanity url).
 *   4. Logged in user AS a contact: as a logged in user in an Organization A,
 *      visiting a room from Organization B.
 *
 * @see https://miro.com/app/board/o9J_lQHsRwE=/?moveToWidget=3458764518916051814&cot=14
 */
const RoomViewPage: React.FC<{ setViewRoom?: (flag: boolean) => void }> = ({
  setViewRoom,
}): JSX.Element => {
  /**
   * 1. Get UUID from URL
   * 2. Get Room settings (query room($uuid!) { settings })
   *    2.1. Is member AND belongs to the room organization? continue and notify
   *    2.2. Room doesn't have security settings? continue directly to the room
   *    2.3. Requires verification? (email, passcode, invited users only)
   *         2.3.1. Try verifying using persisted access (cookie)
   *         2.3.2. An email param was given to grant direct access? try access with this email
   *         2.3.3. Verified? Jump to #3
   *         2.3.4. Prompt user for email and/or passcode
   *    2.4. Confirm access
   * 3. Request Room information (query room($uuid!) { contact, settings, structure }, headers: { [contact_id], organization_id } )
   *    3.1. Is member AND belongs to the room organization? continue
   *    3.2. Contact? set contact id header
   *    3.3. Not contact? error
   */
  const theme = useTheme();
  const { newTheme } = useContext(LayoutContext);

  const { getAllQueryStringParams, updateQuery } = useQuery();
  const { roomUUID } = useParams();
  const { setTitle } = useContext(HeadContext);
  const { t } = useI18n();

  const location = useLocation();
  const emailParam = getEmailQueryString(location);

  const [organization, setOrganization] = useState<OrganizationSchema>();
  const [owner, setOwner] = useState<UserSchema>();
  const [room, setRoom] = useState<RoomSchema>();
  const [roomType, setRoomType] = useState(getRoomType(location));

  const isConsentDeclinedSuppression =
    !!room?.organization?.consentDeclinedSuppression;

  const isBuyerCopilotEnabledForRoom =
    !!room?.organization?.releaseControlBuyerCopilot &&
    !!room?.buyerCopilotEnabled &&
    !!room?.organization?.buyerCopilotEnabled &&
    !!room &&
    !isInternalRoom(room);

  const {
    isVisible,
    openVisibilityWithDefaultMessage,
    closeVisibility,
    toggleVisibility,
    defaultInputMessage,
  } = useCopilotVisibility({
    defaultVisible: false,
    roomUuid: roomUUID,
  });
  const [pendingConversationMessage, setPendingConversationMessage] = useState<
    string | null
  >(null);
  const [openedFromCopilot, setOpenedFromCopilot] = useState(false);
  const [isConversationOpened, setIsConversationOpened] = useState(false);

  const isVendor = isVendorMode();
  const shouldUseUnifiedConsent = isVendor;

  // pre-filled contact data, used in the given scenario that direct access
  // to the room wish to be granted
  const [contactEmail, setContactEmail] = useState(
    isEmail(emailParam) ? emailParam : ''
  );

  // consent states
  const [openConsent, setOpenConsent] = useState(false);
  const [consentSubmit, setConsentSubmit] = useState(0);

  // room state flags
  const [accessAttempts, setAccessAttempts] = useState(0);
  const [accessDenied, setAccessDenied] = useState(false);
  const [userAccessDenied, setUserAccessDenied] = useState(false);
  const [confirmAccess, setConfirmAccess] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [draft, setDraft] = useState(false);
  const [expired, setExpired] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [pinCodeError, setPinCodeError] = useState(false);
  const [verifyingAccess, setVerifyingAccess] = useState(false);
  const [verifyPincode, setVerifyPincode] = useState(false);

  // state control flags
  const [ready, setReady] = useState(false);
  const [isContactVisitor, setIsContactVisitor] = useState(false);
  const [isGuestVisitor, setIsGuestVisitor] = useState(true);

  // error states
  const [deletedError, setDeletedError] = useState(false);
  const [error, setError] = useState(false);

  // folder view state
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);

  // misc
  const [trackingProperties, setTrackingProperties] = useState<EventProperty>(
    {}
  );

  /**
   * Which contact belongs to the current user. This is used in case the
   * room is visited by somebody that isn't an authenticated user that belongs
   * to the organization where the room originated.
   *
   * @note This is important, since setting this value causes the visitor
   * to be treated as a contact (contact-id header will be present in
   * other requests). For authenticated users make sure this value is not set
   */
  const [userContact, setUserContact] = useState<ContactSchema>();
  const folderViewData = useFolderData(currentFolderId, {
    contactId: userContact?.id,
  });

  const sortedCollaborators = useMemo(
    () =>
      (room?.roomCollaborators || []).sort((a, b) =>
        ascendingSort(composeFullname(a), composeFullname(b))
      ),
    [room?.roomCollaborators]
  );

  const {
    mentions: combinedMentions,
    structureContents: copilotContentPool,
    loading: mentionsLoading,
    error: mentionsError,
  } = useRoomContentsPool(room?.id, isVisible, userContact?.id);

  const sellingCollaborators = useMemo(
    () =>
      sortedCollaborators.filter((collaborator) => collaborator.sellingTeam),
    [sortedCollaborators]
  );

  const shouldDisableRoomSummary = useMemo(
    () => isRoomSummaryDisabled(room),
    [room]
  );

  // This is to show header when leaving from view room page

  useEffect(
    () => () => setViewRoom && setViewRoom(false),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /**
   * Consent flag. This is used as a reference to determine whether or not
   * the user has given an answer to the consent prompt. This may come from
   * 2 sources: a cookie or the user's explicit answer to the prompt.
   */
  const consentAccepted = useRef<boolean>();

  const { current: currentUser } = useRef(UserStore.get());
  const { current: isAuthenticatedUser } = useRef(!!currentUser);
  const { current: queryStringParams } = useRef(getAllQueryStringParams());

  const { current: roomContactToken } = useRef(
    queryStringParams[QUERY_STRING.ROOM_CONTACT_TOKEN]
  );

  const { current: downloadSignableId } = useRef(
    queryStringParams[QUERY_STRING.DOWNLOAD_SIGNABLE_ID]
  );

  const { current: referrerParam } = useRef(
    queryStringParams[QUERY_STRING.REFERRER]
  );

  const { current: refreshQueryStringParams } = useRef(
    getRefreshQueryParams(queryStringParams)
  );

  const defaultReferrer = isAuthenticatedUser
    ? Referrers.DEAL_ROOMS.USER_VIEW
    : Referrers.DEAL_ROOMS.CONTACT_VIEW;

  const referrer = referrerParam || defaultReferrer;

  const disableRoomLayout =
    error || deletedError || disabled || draft || expired || accessDenied;

  /**
   * Properties for contextualize contents. Primarily used by URL contents
   * from external provided context (e.g. Asset Hub), to include these
   * properties as query string params, to send additional information, such as
   * where or by whom is it being accessed (e.g. by a contact or an internal user)
   */
  const contentContextProps: RoomContentContextProps = useMemo(
    () => ({
      isContact: isContactVisitor,
      opportunityId: room?.deal?.crmId,
      primarySource: room?.deal?.name || DefaultDealName.NoDeal,
      roomUUID: room?.uuid,
      secondarySource: room?.name,
      visitorId: userContact?.id || currentUser?.id,
    }),
    [isContactVisitor, room, userContact?.id, currentUser?.id]
  );

  useEffect(() => {
    // Handle i18n of cookie consent, it is handled with the user browser language
    setHtmlLang();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setError(false);
        await initialLoadRoom();
      } catch (e) {
        const queryError = e as QueryErrorResponse;
        await handleError(queryError);
      } finally {
        updateQuery({}, true);
      }
    })();

    return () => {
      setTitle('');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (disableRoomLayout) {
      setReady(true);
    }
  }, [disableRoomLayout]);

  // use case for consent flow to get the updated states
  useEffect(() => {
    if (!consentSubmit) {
      return;
    }

    handleGuestContactAccess(room!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentSubmit]);

  const onContactEdited = (updatedContact?: ContactSchema) => {
    setUserContact(updatedContact);
  };

  /**
   * When a signable has been signed, then we update the room's state, in order
   * to reflect the changes on the signable after it has been signed by the
   * current user
   */
  const onSignableSigned = (signable: SignableSchema) => {
    if (!room) {
      return;
    }

    setRoom({
      ...room,
      signables: room.signables?.map((roomSignable) =>
        roomSignable.id !== signable.id ? roomSignable : signable
      ),
    });
  };

  const fetchRoom = (
    contactId?: string,
    anonymizeRequest = false,
    viewfields = roomViewFields
  ) =>
    new RoomsInterface(contactId, anonymizeRequest).getRoom(
      { uuid: roomUUID },
      true,
      viewfields
    );

  const initialLoadRoom = async () => {
    const room = await fetchRoom(undefined, true, roomViewInitialFields);
    const { language, organization } = room;

    if (isDomainRedirectionRequired(room)) {
      return;
    }

    if (unexpectedErrorHandled(room)) {
      // load default language for generic error messages
      // at this point we don't have the room.language
      I18n.setLanguage(undefined, true);
      return;
    }

    // set organization for security verification dialog builder
    if (organization) {
      setOrganization(organization);
    }

    /**
     * Loading the custom organization fonts for the room if they were specified.
     * In order to build the theme and the fonts to be available for the
     * security verification dialog, we need to set the org in the state.
     */
    if (organization?.fonts) {
      await loadOrganizationFonts(organization.fonts);
    }

    /**
     * Setting the context org allows us to determine whether the visitor is
     * a guest or an authenticated user. The main rule for auth users is
     * they must belong to the room's org, otherwise, they'll be treated as
     * guests and must go through the security checks
     */
    const isGuest = setContextOrganization(organization);
    setIsGuestVisitor(isGuest);
    setRoom(room);

    /**
     * Loading room's org languages always as an anonymized request, as there
     * isn't enough info here to land on the proper schema (public, contact or
     * private). The alternative would be loading the translations on other
     * 3 different points, which makes the implementation way harder
     */
    await I18n.setLanguage(language, true);

    /**
     * Loading a disabled, draft or expired room will display a custom message,
     * therefore, there's no need on continuing with other access checks
     */
    if (inaccessibleRoomHandled(room) || expiredRoomHandled(room)) {
      return;
    }

    if (isGuest) {
      handleGuestContactAccess(room);
    } else {
      handleAuthenticatedUserAccess(room);
    }
  };

  /**
   * If non-authenticated users or those that don't belong to the room's org
   * try to access the room, they must be treated as guests; so a contact-id header
   * must be used for room requests instead of an authentication header
   */
  const loadRoomAsContact = async (contact: ContactSchema): Promise<void> => {
    const room = await fetchRoom(contact.id);
    onRoomLoaded(room, contact.anonymous);
  };

  /**
   * For logged in users, there are 2 scenarios:
   *  1. The room is intented for internal use only: access to the room depends
   *     on whether the user is allowed to enter or not
   *  2. The room is for external use: authenticated users belonging to the
   *     room's org are allowed to enter the room without any security checks
   */
  const handleAuthenticatedUserAccess = async (
    room: RoomSchema
  ): Promise<void> => {
    const hasAccessToRoom = await hasGuestUserAccess(room);
    if (!hasAccessToRoom) {
      setUserAccessDenied(true);
      return handleAccessDenied();
    }

    const fetchedRoom = await fetchRoom();
    if (unexpectedErrorHandled(fetchedRoom)) {
      return;
    }

    onRoomLoaded(fetchedRoom);

    /**
     * We display a custom message because the security steps were skipped
     * for users entering rooms from their organizations AND said users
     * aren't guests that need to be tracked
     */
    if (
      fetchedRoom.organization?.isCurrentUserMember &&
      !fetchedRoom.trackActivity
    ) {
      const snackbar = isInternalRoom(fetchedRoom)
        ? 'This visit is not tracked since you are a room owner/collaborator'
        : 'Some security authorization steps may have been skipped because you belong to this organization.';

      SnackbarStore.show(snackbar);
    }
  };

  // get consent value from cookie if there is present
  const setContactConsent = () => {
    const data = getConsent();
    if (data) {
      consentAccepted.current = data.consent === CONSENT.ACCEPTED;
    }
  };

  /**
   * For guests, the flow is more complicated, since they could be:
   *  1. Enable Us users belonging to an org different than the room's org
   *  2. people that need to enter their email to access the room
   *  3. anonymous guests, for public rooms
   *  4. verified & known guests that have already visited the room and have
   *     a cookie with access information, to make subsequent accesses easier
   * Given the complexity, there are numerous steps that must be done in
   * order to end up with a verified contact to grant access to the room
   * by adding the contact-id header
   */
  const handleGuestContactAccess = async (room: RoomSchema): Promise<void> => {
    setIsContactVisitor(true);

    const { organization: org, verifyEmail } = room;
    const isPublicRoom = isRoomPublic(room);

    /**
     * If the room is being loaded by a non-user (admin/salesperson) contact,
     * we first attempt to access the room with data from the room cookie
     */
    const [persistedAccessData, identifiedContact] =
      await authenticateWithPersistedAccessData();

    const currentIsVendor = isVendorMode();
    const currentShouldUseUnifiedConsent = currentIsVendor;

    if (org?.inhouseConsent) {
      if (currentShouldUseUnifiedConsent) {
        const shouldShowConsentDialog = await isConsentDialogRequired(
          org,
          identifiedContact,
          room,
          currentIsVendor
        );

        if (shouldShowConsentDialog) {
          return;
        }
      } else {
        // Original consent flow for non-vendor or when feature flag is disabled
        const promptConsent = await requiresConsent(
          org,
          identifiedContact,
          room,
          currentShouldUseUnifiedConsent,
          currentIsVendor
        );
        if (promptConsent) {
          return;
        }
      }
    }

    /**
     * At this point, we were able to verify the identify of the contact based
     * on the room cookie, so we continue the load room flow as said contact
     */
    if (identifiedContact) {
      return loadRoomAsContact(identifiedContact);
    }

    /**
     * If there is a cookie access error in a public room, it's probably because
     * something unexpected. This could be possible if a cookied anonymous contact
     * attempted to access a room which security settings were changed, requiring
     * an email, passcode or different allowed guests
     */
    if (persistedAccessData && isPublicRoom) {
      handleAccessDenied();
      return;
    }

    /**
     * If the room has a contact token, it means that the user is trying to
     * access the room directly via a customized link for the contact, so we can
     * skip the verification. These tokens are generated specifically for
     * each contact and they are only valid for the room they were generated for.
     * E.g.: share room via SMS
     */
    const canSkipVerificationPromptWithContactToken = !!roomContactToken;

    /**
     * If it's a public room (no email and no passcode), we request
     * an anonymous contact. But if an email query param is given and the
     * room doesn't require password OR if there's an access token for the contact,
     * we verify the room access with the given param (email or access token), to
     * obtain the contact that's accessing the room
     */
    if (
      (isPublicRoom && !verifyEmail) ||
      canSkipVerificationPromptWithContactToken
    ) {
      // let it decide the access confirmation depending on the verifyEmail flag
      // as long as there is no contact access token present
      const shouldVerifyEmail =
        verifyEmail && !canSkipVerificationPromptWithContactToken;

      const email = contactEmail.length > 0 ? contactEmail : undefined;
      const verifiedContact = await onRoomAccessAttempt(
        { email, roomContactToken },
        shouldVerifyEmail
      );

      /**
       * There are 2 scenarios here:
       *   - A contact was successfully verified (verifiedContact?.contact)
       *   - The contact requires email verification, which is only true if the
       *     room has the verifyEmail flag
       * If any of the scenarios is true, there's an early return, since the
       * prompts are handled by the `onRoomAccessAttempt` method
       */
      if (verifiedContact?.contact || verifiedContact?.pendingVerification) {
        return;
      }
    }

    /**
     * If the room requires email/passcode or the previous verification with
     * an email parameter didn't give a verified contact, we prompt
     * the visitor for their access information. When the access information
     * is given and verified, another attempt to load the room as the
     * verified contact is made, which is handled by `onSecuritySettingsSubmit`
     */
    const requiresAccessConfirmation = !isPublicRoom || verifyEmail;
    setConfirmAccess(requiresAccessConfirmation);

    if (requiresAccessConfirmation) {
      // set room owner for security dialog to display owner fullname for shared links
      setOwner(room.owner);
    }

    /**
     * Track that the room required an email to enter only if the user
     * is going to be prompted with the input to enter their email, whether
     * it's because they haven't previously entered the room or if they
     * had cookied access credentials and they are outdated
     */
    if (requiresAccessConfirmation && room.requiresEmail) {
      Analytics.track(Events.DEAL_ROOM_EMAIL_REQUESTED, {
        [Properties.CONTACT_NUMBER]: room?.roomContactsCount,
        [Properties.ENTERED_FROM]: Referrers.DEAL_ROOMS.ACTIVITY.ENTER,
        [Properties.HAS_DEAL]: !!room?.deal,
      });
    }
  };

  /**
   * This method is called after a room has been successfully loaded, whether
   * it's because a logged user access one org within their organization OR
   * a contact has been verified and granted access to the room. This
   * finally opens the room for the visitor.
   */
  const onRoomLoaded = async (room: RoomSchema, anonymousVisitor = false) => {
    /**
     * Update the guest visitor flag, to enable tracking if the room requires it
     * for the following scenarios:
     *  - Internal rooms: authenticated user is a room guest
     *  - External rooms: guests or authenticated users visiting rooms from
     *    other orgs where they don't belong
     */
    setIsGuestVisitor((prev) => room.trackActivity || prev);

    setRoomFields(room);

    // tracking succesful DR visits
    const trackingProps = getTrackingProperties(room, {
      anonymous: anonymousVisitor,
      contactEmail: !!contactEmail,
      extended: true,
      referrer: getTrackingReferrer(),
      status: room.status,
    });

    const eventTrackingViewed = getViewedEvent(room);
    Analytics.track(eventTrackingViewed, trackingProps);

    /**
     * Keeping a reference to the tracking properties, to inject them to
     * lower-level components that need to track data with the room's details
     */
    setTrackingProperties(trackingProps);

    /**
     * Finally, everything has been loaded successfully and it's ready to
     * display the room, so it's time to turn on the flags
     */
    setAccessDenied(false);
    setConfirmAccess(false);
    setVerifyPincode(false);
    setReady(true);
  };

  /**
   * Performs a call to verify the room access with the current authenticated
   * user, only for rooms intended for organization's internal audience. If
   * an error or an empty user is returned, the access to the room is denied
   * and the execution flow stops, displaying an error message
   */
  const hasGuestUserAccess = async (room: RoomSchema): Promise<boolean> => {
    if (!isInternalRoom(room)) {
      return true;
    }

    try {
      const response = await new RoomsInterface().verifyRoomAccess(
        { uuid: roomUUID },
        verifyRoomUserAccessFields
      );

      return !!response.verifyRoomAccess.user;
    } catch (e) {
      processVerifyAccessErrors(e);
      return false;
    }
  };

  /**
   * Gets the contact and room access information from the stored cookie.
   * If that info exists, then it tries once to verify that info and
   * grants access to the room if that info is up to date and functional.
   *
   * If the cookie info belongs to an anonymous contact, the verification
   * step is skipped, since that contact has already entered the room
   * and there is no other way to associate the session with the same anonymous
   * contact.
   */
  const authenticateWithPersistedAccessData = async (): Promise<
    [PersistedAccessData | undefined, ContactSchema | undefined]
  > => {
    const persistedAccessData = RoomSecurity.getAccess(roomUUID);
    if (!persistedAccessData) {
      return [undefined, undefined];
    }

    const { contact, email, passcode, roomContactToken } = persistedAccessData;

    const verifiedContact = await handleAccessConfirmation({
      contactId: contact?.id,
      email,
      passcode,
      roomContactToken,
    });

    return [persistedAccessData, verifiedContact];
  };

  const isConsentDialogRequired = async (
    org: OrganizationSchema,
    identifiedContact: ContactSchema | undefined,
    room: RoomSchema,
    isInVendorMode?: boolean
  ): Promise<boolean> => {
    if (!org?.inhouseConsent) {
      return false;
    }

    // Skip consent dialog when passcode is required (unified flow handles consent)
    if (room?.requiresPasscode) {
      return false;
    }

    const promptConsent = await requiresConsent(
      org,
      identifiedContact,
      room,
      true,
      isInVendorMode
    );
    return promptConsent;
  };

  const requiresConsent = async (
    organization: OrganizationSchema,
    contact?: ContactSchema,
    room?: RoomSchema,
    shouldUseUnifiedConsent?: boolean,
    isInVendorMode?: boolean
  ): Promise<boolean> => {
    setContactConsent();

    // Skip consent dialog when passcode is required (unified flow handles consent)
    if (shouldUseUnifiedConsent && room?.requiresPasscode) {
      return false;
    }

    if (getConsent()) {
      return false;
    }

    const isConsentEmpty = consentAccepted.current === undefined;

    // scenario when the consent cookie expired or contact already answered the consent
    if (isConsentEmpty && contact && contact.consent !== null) {
      consentAccepted.current = contact.consent;
      persistConsent({
        consent: contact.consent ? CONSENT.ACCEPTED : CONSENT.REJECTED,
      });
    }

    // scenario if consent is empty or the identified contact hasn't been answered before
    if (isConsentEmpty || (contact && contact.consent === null)) {
      // check if consent needs to be prompted based on the region from where
      // the contact is visiting and the room's organization settings
      const response = await new ConsentInterface(undefined, true).consent({
        organizationId: organization.id,
      });

      setContactConsent();
      if (getConsent()) {
        return false;
      }

      // display consent prompt only if geolocation of the contact is allowed in
      // organization.geo_target_consent
      if (response.displayConsent) {
        if (!isInVendorMode) {
          Analytics.track(Events.CONSENT_VIEWED, {
            [Properties.VENDOR_ID]: organization.vendorId,
          });
        }

        if (shouldUseUnifiedConsent && room?.requiresEmail) {
          return false;
        }

        // Track DSR consent viewed for public rooms with unified consent
        if (
          shouldUseUnifiedConsent &&
          !room?.requiresEmail &&
          !room?.requiresPasscode
        ) {
          Analytics.track(Events.DSR_CONSENT_VIEWED, {
            type: UnifiedConsentType.ONLY_CONSENT,
          });
        }

        setOpenConsent(true);
        return true;
      }
    }

    return false;
  };

  const acceptConsent = () => {
    consentAccepted.current = true;
    persistConsent({ consent: CONSENT.ACCEPTED });
  };

  const onActivityDataConsentDecision = (accepted: boolean) => {
    if (accepted) {
      acceptConsent();
    } else {
      consentAccepted.current = false;
      persistConsent({ consent: CONSENT.REJECTED });
    }
  };

  /**
   * Performs a request with the access data (email, passcode or a known
   * contact id) to verify if the visitor has access to the room. If it's
   * a known contact (contactId given), we get that contact info, otherwise
   * a contact is provided if an email address is given or an anonymous
   * contact otherwise
   */
  const verifyAccess = async (data: AccessConfirmationData) => {
    setVerifyingAccess(true);

    try {
      const { contactId, ...accessData } = data;
      const anonymizeRequest = !contactId;

      setContactConsent();
      let consentValue = consentAccepted.current;
      if (
        accessData.email?.length &&
        consentValue === undefined &&
        !shouldUseUnifiedConsent
      ) {
        consentValue = true;
        acceptConsent();
      }

      const { verifyRoomAccess } = await new RoomsInterface(
        contactId,
        anonymizeRequest
      ).verifyRoomAccess({
        uuid: roomUUID,
        ...accessData,
        consent: consentValue,
      });

      return verifyRoomAccess;
    } catch (e) {
      processVerifyAccessErrors(e);
      return { pendingVerification: false };
    } finally {
      setVerifyingAccess(false);
    }
  };

  const processVerifyAccessErrors = (e: unknown) => {
    const err = e as QueryErrorResponseMessage;
    if (room && err?.[0]?.extensions?.code === ErrorCodeList.RoomAccessDenied) {
      trackDeniedAccess(
        room.audience,
        referrer,
        err[0].extensions.attribute || ''
      );
    }
  };

  /**
   * Handles access attempts and tries to verify the given access info
   * to obtain a verified contact; in case one is obtained, a cookie
   * is stored in order for that contact to seemlessly access the room
   * in subsequent visits
   */
  const handleAccessConfirmation = async (
    data: AccessConfirmationData
  ): Promise<ContactSchema | undefined> => {
    try {
      const attempts = accessAttempts + 1;
      setAccessAttempts(attempts);

      const { contact } = await verifyAccess(data);

      setAccessDenied(!contact);
      if (contact) {
        setConfirmAccess(false);
        RoomSecurity.persistAccess(roomUUID, { contact, ...data });
        setUserContact(contact);
        return contact;
      }

      // block any more attempts (until the user refreshes)
      if (attempts >= MAX_ACCESS_ATTEMPTS) {
        setConfirmAccess(false);
        setAccessDenied(true);
      }

      return undefined;
    } catch (e) {
      setAccessDenied(true);
      return undefined;
    }
  };

  /**
   * Handles access via PIN code and tries to verify the given access info
   * to obtain a verified contact; in case one is obtained, a cookie
   * is stored in order for that contact to seemlessly access the room
   * in subsequent visits, also checks the pendingVerification flag to display
   * verification PIN code dialog
   */
  const handlePinAccessConfirmation = async (
    data: AccessConfirmationData
  ): Promise<RoomAccessData | undefined> => {
    try {
      setAccessDenied(false);
      const result = await verifyAccess(data);
      const { contact, pendingVerification } = result;

      if (contact) {
        setConfirmAccess(false);
        setVerifyPincode(false);
        RoomSecurity.persistAccess(roomUUID, { contact, ...data });
        setUserContact(contact);
      }

      if (!pendingVerification) {
        setAccessDenied(true);
      }

      return result;
    } catch (e) {
      return undefined;
    }
  };

  /**
   * If user calls wrong email action:
   *  - Reset pin code error
   *  - Open security dialog and hide verify pin code dialog
   */
  const handleWrongEmail = () => {
    setPinCodeError(false);
    setConfirmAccess(true);
    setVerifyPincode(false);
  };

  /**
   * Scenario where the visitor has been denied access to the room, which could
   * be one of the following reasons:
   *  1. If a visitor comes back and has no access to the room, because the
   *     stored cookie didn't grant access (e.g. security settings changed since last time)
   *  2. An authenticated user tried to access a room for organization's internal
   *     user only, and they aren't allowed to visit the room
   */
  const handleAccessDenied = () => {
    setAccessAttempts(MAX_ACCESS_ATTEMPTS);
    setAccessDenied(true);
    setConfirmAccess(false);
  };

  const handleError = async (queryError: QueryErrorResponse) => {
    // load default language
    await I18n.setLanguage(undefined, true);
    if (Array.isArray(queryError)) {
      const msg = queryError[0]?.message;
      // make sure this error matches the one from the backend
      if (msg === ErrorCodeList.RoomDeleted) {
        setDeletedError(true);
        return;
      }
    }

    setError(true);
  };

  const unexpectedErrorHandled = (room: RoomSchema | null | undefined) => {
    const hasError = !room;
    setError(hasError);
    return hasError;
  };

  const inaccessibleRoomHandled = (room: RoomSchema) => {
    const isDisabled = isRoomDisabled(room);
    const isDraft = isDraftRoom(room);
    const inaccessible = isDisabled || isDraft;

    const eventTrackingAttempted = getViewAttemptedEvent(room);

    if (inaccessible) {
      Analytics.track(
        eventTrackingAttempted,
        getTrackingProperties(room, {
          contactEmail: !!contactEmail,
          referrer: getTrackingReferrer(),
          status: room.status,
        })
      );
    }

    setDisabled(isDisabled);
    setDraft(isDraft);

    return inaccessible;
  };

  const expiredRoomHandled = (room: RoomSchema) => {
    const isExpired = room.expired;
    const eventTrackingAttempted = getViewAttemptedEvent(room);

    if (isExpired) {
      Analytics.track(
        eventTrackingAttempted,
        getTrackingProperties(room, {
          contactEmail: !!contactEmail,
          referrer: getTrackingReferrer(),
          status: 'expired',
        })
      );
    }

    setExpired(isExpired);
    return isExpired;
  };

  const setContextOrganization = (
    org: OrganizationSchema | undefined
  ): boolean => {
    if (!org) {
      return true;
    }

    /**
     * Adding the room's org as the current one in use for the app's context,
     * since this will allow the proper header to be used for the current session
     */
    OrganizationsStore.add(org);
    OrganizationsStore.setCurrent(org.id);

    if (!isAuthenticatedUser) {
      return true;
    }

    /**
     * Having an active role within the room's org grants the current
     * authenticated user a special treatment: allowing them to skip
     * the room's security checks and avoiding to trigger content tracking;
     * since they are allowed to view their own org's rooms seemlessly.
     */
    const userRole = UserRolesStore.getRoleByOrganizationId(org.id);
    if (userRole?.status !== UserStatus.ACTIVE) {
      return true;
    }

    UserRolesStore.setCurrent(userRole);
    return false;
  };

  const setRoomFields = (room: RoomSchema) => {
    setOrganization(room.organization);
    setTitle(room.organization?.pageTitle || '');

    setRoomType(room.roomType as RoomType);
    setRoom(room);
  };

  const getTrackingReferrer = () => {
    if (referrerParam) {
      return referrerParam;
    }
    if (contactEmail) {
      return Referrers.DEAL_ROOMS.EMAIL;
    }
    if (roomContactToken) {
      return Referrers.DEAL_ROOMS.SMS;
    }
    return defaultReferrer;
  };

  // common method to call loadRoomAsContact
  const loadContactRoom = (verifiedContact: ContactSchema) => {
    loadRoomAsContact(verifiedContact);

    Analytics.track(Events.DEAL_ROOM_EMAIL_PROVIDED, {
      [Properties.CONTACT_NUMBER]: room?.roomContactsCount,
      [Properties.ENTERED_FROM]: Referrers.DEAL_ROOMS.ACTIVITY.ENTER,
      [Properties.HAS_DEAL]: !!room?.deal,
    });
  };

  const onRoomAccessAttempt = async (
    data: AccessConfirmationData,
    verifyEmail: boolean
  ) => {
    let verifiedContact = null;
    let verifiedPendingVerification = false;

    // if room.verifyEmail is on, use the PIN acces flow
    if (verifyEmail) {
      const result = await handlePinAccessConfirmation(data);
      verifiedContact = result?.contact;
      verifiedPendingVerification = result?.pendingVerification || false;
    } else {
      verifiedContact = await handleAccessConfirmation(data);
    }

    const resultFormatted = {
      contact: verifiedContact,
      pendingVerification: verifiedPendingVerification,
    };

    // if contact is returned, load the room
    if (verifiedContact) {
      loadContactRoom(verifiedContact);
      return resultFormatted;
    }

    // if pendingVerification is true,
    // save email/pass to call verify access and open verify pin code dialog
    if (verifiedPendingVerification) {
      setContactEmail(data.email!);
      setPasscode(data.passcode!);
      setConfirmAccess(false);
      setVerifyPincode(true);
      return resultFormatted;
    }
  };

  const onSecuritySettingsSubmit = async (data: AccessConfirmationData) =>
    onRoomAccessAttempt(data, room?.verifyEmail === true);

  // handle the action of submit the PIN code
  const onVerifyPinCodeSubmit = async (
    data: AccessConfirmationData,
    onError?: () => void
  ) => {
    const result = await handlePinAccessConfirmation(data);

    // if contact is not returned, it means the PIN code is incorrect
    if (!result?.contact) {
      setAccessDenied(false);
      setPinCodeError(true);
      if (onError) {
        onError();
      }
      return;
    }

    setPinCodeError(false);
    loadContactRoom(result.contact);
  };

  // handle the action of resend the PIN code
  const onResendPin = async (data: AccessConfirmationData) => {
    setPinCodeError(false);
    await handlePinAccessConfirmation(data);
  };

  const trackConsent = (event: Event) => {
    Analytics.track(event, {
      [Properties.ENTERED_FROM]: getTrackingReferrer(),
      [Properties.VENDOR_ID]: organization?.vendorId,
    });
  };

  const onAcceptConsent = async () => {
    acceptConsent();
    setOpenConsent(false);
    setConsentSubmit(Date.now());
    if (!isVendor) {
      trackConsent(Events.CONSENT_ACCEPTED);
    }

    if (
      shouldUseUnifiedConsent &&
      !room?.requiresEmail &&
      !room?.requiresPasscode
    ) {
      Analytics.track(Events.DSR_CONSENT_ACCEPTED, {
        type: UnifiedConsentType.ONLY_CONSENT,
      });
    }
  };

  const onRejectConsent = async () => {
    consentAccepted.current = false;
    // if consent is rejected, create the contact as anonymous
    setContactEmail('');
    setOpenConsent(false);
    persistConsent({ consent: CONSENT.REJECTED });
    setConsentSubmit(Date.now());
    if (!isVendor) {
      trackConsent(Events.CONSENT_REJECTED);
    }
  };

  const getScrollThresholdBase = () => {
    let threshold = 0;
    if (room?.bannerUri) {
      threshold += getNumericSpacing(theme, 30);
    }

    if (room?.name) {
      threshold += getNumericSpacing(theme, 9);
    }

    return threshold;
  };

  const renderContentAcl = (structureContent: RoomJSONStructureItem) => {
    const contentPermisionEnabled = room && !isInternalRoom(room);

    if (!contentPermisionEnabled) {
      return;
    }

    const { id, type } = structureContent;
    const isTextBox = type === RoomStructureItemType.TextBox;
    const objectAclsContents = buildRoomObjectAclsFromContentIds(
      room.roomObjectAclsContents
    );
    const contentObjectAcls = getObjectAclsForContent(
      id,
      type,
      objectAclsContents
    );

    if (!contentObjectAcls?.length) {
      return;
    }

    return (
      <ContentAccessList
        contentId={structureContent.id}
        contextProps={contentContextProps}
        disablePortal
        loadFromResource
        objectAcls={contentObjectAcls}
        positionStyle={{ zIndex: 1, left: isTextBox ? 2 : undefined }}
        roomId={room.id}
        tileMode
      />
    );
  };

  const renderLayoutContents = () => {
    if (!ready) {
      return <RoomLayoutSkeleton roomType={roomType} />;
    }

    if (error) {
      return (
        <RoomEmptyState
          primaryText={t('room.error_loading')}
          room={room}
          retry
          secondaryText={t('room.retry')}
        />
      );
    }

    if (deletedError) {
      return <RoomEmptyState primaryText={t('room.deleted')} room={room} />;
    }

    const secondaryMessage = t('room.inaccessible');

    if (disabled) {
      return (
        <RoomEmptyState
          primaryText={t('room.disabled')}
          room={room}
          secondaryText={secondaryMessage}
        />
      );
    }

    if (expired || draft) {
      return (
        <RoomEmptyState
          primaryText={t('room.expired')}
          room={room}
          secondaryText={secondaryMessage}
        />
      );
    }

    if (
      !verifyingAccess &&
      accessAttempts >= MAX_ACCESS_ATTEMPTS &&
      accessDenied
    ) {
      const primaryMsgKey = userAccessDenied
        ? 'room.access_denied.user_access_denied'
        : 'room.access_denied.default';
      const secondaryMsgKey = userAccessDenied
        ? 'room.access_denied.try_again.user_access_denied'
        : 'room.access_denied.try_again.default';

      return (
        <RoomEmptyState
          primaryText={t(primaryMsgKey)}
          retry={!userAccessDenied}
          room={room}
          secondaryText={t(secondaryMsgKey)}
        />
      );
    }

    return (
      <WindowFocusContextProvider>
        <FullScreenTrackingContextProvider>
          <RoomViewController
            actionable
            ActionPlanEditorProps={{
              allowInvitations: !isInternalRoom(room!),
            }}
            collaborators={sortedCollaborators}
            contextProps={contentContextProps}
            copilotContentPool={copilotContentPool}
            currentFolderId={currentFolderId}
            folderViewData={folderViewData}
            disabled={disableRoomLayout}
            initialConversationMessage={pendingConversationMessage}
            isContactVisitor={isContactVisitor}
            onContactEdited={onContactEdited}
            onConversationMessageConsumed={() => {
              setPendingConversationMessage(null);
            }}
            onFolderOpen={(folderId) => setCurrentFolderId(folderId)}
            onFolderBack={() => {
              const crumbs = folderViewData?.breadcrumbs ?? [];
              const parentId =
                crumbs.length > 1 ? crumbs[crumbs.length - 2].id : null;
              setCurrentFolderId(parentId);
            }}
            onConversationDialogOpened={() => {
              setIsConversationOpened(true);
              closeVisibility();
            }}
            onConversationDialogClosed={() => {
              setIsConversationOpened(false);
              if (openedFromCopilot) {
                openVisibilityWithDefaultMessage('');
                setOpenedFromCopilot(false);
              }
            }}
            referrer={referrer}
            renderContentAcl={renderContentAcl}
            room={room}
            roomType={roomType}
            sellingCollaborators={sellingCollaborators}
            isBuyerCopilotEnabledForRoom={isBuyerCopilotEnabledForRoom}
            SignablesProps={{
              disableChangesOnScroll: !!room?.signables?.length,
              downloadSignableId,
              onSignableSigned,
              scrollThresholdBase: getScrollThresholdBase(),
            }}
            trackingProperties={trackingProperties}
            userContact={userContact}
          >
            {(wrapperApi) => (
              <RoomLayout
                {...wrapperApi}
                disableRoomSummary={shouldDisableRoomSummary}
                layout={room?.layout || RoomLayoutEnum.Story3Columns}
                RoomContainerProps={{
                  disableFooter: disableRoomLayout,
                  disableStickiness: organization?.disableRoomStickyColumns,
                }}
              />
            )}
          </RoomViewController>
          {/* Inside the tracking provider so the copilot hides while a
              full-screen surface (MAP editor, full-screen content) covers
              the room. Renders through portals, so placement is unaffected */}
          {isBuyerCopilotEnabledForRoom && !isConversationOpened && (
            <GlobalCopilot
              roomId={room?.id!}
              isConversationEnabled={
                room && !!room?.enableQa && roomType !== RoomType.ContentLink
              }
              cname={room?.organization?.vendorTenantId!}
              tenantId={room?.organization?.tenantId!}
              roomUuid={roomUUID}
              onContactEdited={onContactEdited}
              author={getConversationAuthor(userContact)}
              isVisible={isVisible}
              openVisibilityWithDefaultMessage={
                openVisibilityWithDefaultMessage
              }
              closeVisibility={closeVisibility}
              toggleVisibility={toggleVisibility}
              defaultInputMessage={defaultInputMessage}
              mentions={combinedMentions}
              mentionsLoading={mentionsLoading}
              mentionsError={mentionsError}
              onPostToGroupChat={(message) => {
                setPendingConversationMessage(message);
                setOpenedFromCopilot(true);
              }}
            />
          )}
        </FullScreenTrackingContextProvider>
      </WindowFocusContextProvider>
    );
  };

  const renderView = () => (
    <OrganizationThemeContextProvider
      colors={organization?.colors}
      fonts={organization?.fonts}
    >
      <ConfirmProvider>
        <RoomViewContextProvider
          shouldUseUnifiedConsent={shouldUseUnifiedConsent}
          isConsentDeclinedSuppression={isConsentDeclinedSuppression}
          contact={userContact}
          hasDeal={!!room?.deal}
          isGuestVisitor={isGuestVisitor}
          queryStringParams={refreshQueryStringParams}
          roomContactsCount={room?.roomContactsCount}
          roomId={room?.id!}
          uuid={roomUUID}
          welcomeMessage={room?.welcomeMessage}
        >
          {renderLayoutContents()}
        </RoomViewContextProvider>
        <SecurityDialogWrapper
          contact={room?.contact}
          isConsentDeclinedSuppression={isConsentDeclinedSuppression}
          displayConsent={
            shouldUseUnifiedConsent &&
            room?.requiresEmail &&
            !room?.requiresPasscode
          }
          email={contactEmail}
          shouldUseUnifiedConsent={shouldUseUnifiedConsent}
          hasError={accessDenied}
          invitedContactsOnly={room?.invitedContactsOnly === true}
          loading={verifyingAccess}
          onActivityDataConsentDecision={
            isConsentDeclinedSuppression
              ? onActivityDataConsentDecision
              : undefined
          }
          onSubmit={onSecuritySettingsSubmit}
          open={confirmAccess}
          owner={owner}
          requiresEmail={room?.requiresEmail === true}
          requiresPasscode={room?.requiresPasscode === true}
          roomType={roomType}
          verifyEmail={room?.verifyEmail === true}
        />
        <VerifyPinCodeDialog
          email={contactEmail}
          hasError={pinCodeError}
          loading={verifyingAccess}
          onSubmit={onVerifyPinCodeSubmit}
          onWrongEmail={handleWrongEmail}
          open={verifyPincode}
          passcode={passcode}
          onResendPin={onResendPin}
        />
        <ConsentPrompDialog
          shouldUseUnifiedConsent={shouldUseUnifiedConsent}
          isConsentDeclinedSuppression={isConsentDeclinedSuppression}
          onAccept={onAcceptConsent}
          onReject={onRejectConsent}
          open={openConsent}
        />
      </ConfirmProvider>
    </OrganizationThemeContextProvider>
  );

  const providerTheme = newTheme ? MindTickleThemeV2 : MindTickleTheme;
  return isVendorMode() ? (
    <ThemeProvider theme={providerTheme}>
      <CopilotActionProvider>
        <CopilotMethodsProvider
          key={
            userContact?.id != null && userContact.id !== ''
              ? String(userContact.id)
              : 'no-visitor-contact'
          }
          visitorContactId={userContact?.id}
          openVisibilityWithDefaultMessage={openVisibilityWithDefaultMessage}
          isVisible={isVisible}
        >
          {renderView()}
        </CopilotMethodsProvider>
      </CopilotActionProvider>
    </ThemeProvider>
  ) : (
    renderView()
  );
};

export default RoomViewPage;
